import {englishLabel, FormatLabel, LabelDescriptor} from './projectStatusLabels';

/**
 * The project-stage vocabulary, deliberately in its own module beside `projectStatusLabels`.
 *
 * A project has BOTH a lifecycle Stage and a RAG Status, and until this was settled a reader met
 * two different concepts both called Status. Stage reads Opportunity / Planning / Running / Halted /
 * Done, Status reads On Track / At Risk / Off Track / em dash. The two files are separate so the
 * distinction is visible in the imports, not just in a comment.
 */

/** The five values `projectStatus.stage` is published with, as `project_status.json` spells them
 * and in the order it lists them. */
const STAGE_WIRE_VALUES = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'] as const;

/** Wire token -> message. The stages are the app's stage profiles, so all five REUSE the app's
 * profile labels (`project_stage_profile.*`, the same keys the stage settings page and the filter
 * chips use) rather than declaring a parallel set. That is not only de-duplication: those keys are
 * translated into every locale we ship, so a Danish reader sees `I gang` here, where a fresh
 * `ai_report_builder.*` key would ship the English default until a translator got to it.
 *
 * A `Map` rather than an object literal because the key is a raw wire string: an object lookup
 * answers for `constructor`, `toString` and `__proto__` off the prototype chain, and formatting one
 * of those as a descriptor yields `undefined` where the type promises a string. */
const STAGE_MESSAGE = new Map<string, LabelDescriptor>([
	['OPPORTUNITY', {id: 'project_stage_profile.opportunity', defaultMessage: 'Opportunity'}],
	['PLANNING', {id: 'project_stage_profile.planning', defaultMessage: 'Planning'}],
	['RUNNING', {id: 'project_stage_profile.running', defaultMessage: 'Running'}],
	['HALTED', {id: 'project_stage_profile.halted', defaultMessage: 'Halted'}],
	['DONE', {id: 'project_stage_profile.done', defaultMessage: 'Done'}],
]);

/**
 * What a reader is shown for a raw `projectStatus.stage` value.
 *
 * An unrecognised token is echoed rather than replaced: unlike an unusable RAG value - which must
 * never be presented as a state - a stage this client has not heard of still names a real project
 * state, and showing the token tells the reader more than hiding it. It is echoed verbatim rather
 * than translated, because there is no message to translate it with. A missing stage answers with
 * an empty string, which is this function saying it has no label rather than a label that happens to
 * be blank - the caller decides how that reads, and the row mapper shows it as `N/A`.
 */
export function stageLabelFor(wireValue: unknown, formatLabel: FormatLabel = englishLabel): string {
	if (typeof wireValue !== 'string' || wireValue === '') {
		return '';
	}
	const message = STAGE_MESSAGE.get(wireValue);
	return message ? formatLabel(message) : wireValue;
}

/** The five stages as a picker's candidates: the wire token as the id a condition stores, the
 * display label beside it. */
export function projectStageCandidates(formatLabel: FormatLabel = englishLabel): {id: string; label: string}[] {
	return STAGE_WIRE_VALUES.map(value => ({id: value, label: stageLabelFor(value, formatLabel)}));
}
